import React, { useEffect, useState } from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import { CustomDialogContent } from '..'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Button,
  Grid,
  capitalize,
} from '@material-ui/core'
import { IDose } from 'daily-dose-common'
import { firestore } from '../../firebase-init'
import * as Utilities from '../../utilities'
import { AddDoseToCirclesOptionModal } from '../../modals'

// Max doses to query per page
const LIMIT = 7
const LOAD_MORE_DOSES = 5

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    heading: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightRegular,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  })
)

interface IFindSuggestionsProps {
  onCancel: () => void
}

const FindSuggestions = ({ onCancel }: IFindSuggestionsProps) => {
  const RANDOM_NUMBER = Math.floor(
    Math.random() * Utilities.KEYS_SUGGESTED_DOSES.length
  )
  const RANDOM_DIRECTION = Math.random() < 0.5 ? 'asc' : 'desc'
  const classes = useStyles()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [query, setQuery] = useState<{
    order: any
    direction: 'asc' | 'desc'
  }>({
    order: Utilities.KEYS_SUGGESTED_DOSES[RANDOM_NUMBER],
    direction: RANDOM_DIRECTION,
  })

  const suggestedDosesRef = firestore
    .collection('suggested-doses')
    .orderBy(query.order, query.direction)

  const [expanded, setExpanded] = React.useState<string | false>(false)
  const [suggestedDoses, setSuggestedDoses] = useState([] as IDose[])
  const [loading, setLoading] = useState(true)
  const [dosesEnd, setDosesEnd] = useState(false)
  const [selectedDose, setSelectedDose] = useState<IDose | null>(null)

  const [open, setOpen] = React.useState(false)

  const handleClickOpen = (dose) => {
    setOpen(true)
    setSelectedDose(dose)
  }

  const handleClose = () => {
    setOpen(false)
    setSelectedDose(null)
  }

  useEffect(() => {
    const fetchSuggestedDoses = async () => {
      const suggestedDosesQuery = suggestedDosesRef.limit(LIMIT)
      const doses: IDose[] = (await suggestedDosesQuery.get()).docs.map(
        Utilities.collectIdAndDocs
      )
      setSuggestedDoses(doses)
      setLoading(false)
    }

    fetchSuggestedDoses()

    // eslint-disable-next-line
  }, [])

  const handleChange = (panel: string) => (
    // eslint-disable-next-line @typescript-eslint/ban-types
    event: React.ChangeEvent<{}>,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false)
  }

  const getMoreSuggestedDoses = async () => {
    setLoading(true)
    const last = suggestedDoses[suggestedDoses.length - 1]
    const cursor = last[query.order]
    const commentsQuery = suggestedDosesRef.startAfter(cursor).limit(LOAD_MORE_DOSES)

    const newDoses = (await commentsQuery.get()).docs.map(Utilities.collectIdAndDocs)

    setSuggestedDoses(suggestedDoses.concat(newDoses))
    setLoading(false)

    if (newDoses.length < LOAD_MORE_DOSES) {
      setDosesEnd(true)
    }
  }

  const onUseSuggestedDose = (dose: IDose) => {
    //TODO: In the future, add dose directly if user has one circle
    return handleClickOpen(dose)
  }

  const renderSuggestedDoses = () => {
    return suggestedDoses.map((suggestion, index) => (
      <Accordion
        expanded={expanded === `${suggestion.name}-${index}`}
        onChange={handleChange(`${suggestion.name}-${index}`)}
        key={suggestion.id}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <Typography className={classes.heading} color="textPrimary">
            {capitalize(suggestion.name)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid
            container
            direction="column"
            justify="center"
            alignContent="center"
            spacing={2}
          >
            <Grid item>
              <Typography align="center" color="textSecondary">
                {capitalize(suggestion.description)}
              </Typography>
            </Grid>
            <Grid item container direction="column" alignItems="flex-end">
              <Button color="primary" onClick={() => onUseSuggestedDose(suggestion)}>
                Use
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    ))
  }

  // TODO: Refactor this. Create a custom Dialog component
  return (
    <CustomDialogContent title="Suggested Doses" onCancel={onCancel}>
      <Grid container spacing={1} className={classes.root}>
        <Grid item xs={12}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            Choose your favorite dose and add it to your group(s).
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {renderSuggestedDoses()}
          {!loading && !dosesEnd && suggestedDoses.length > 1 && (
            <Button
              onClick={getMoreSuggestedDoses}
              color="primary"
              style={{ textTransform: 'none' }}
            >
              Get more suggestions
            </Button>
          )}

          {loading && (
            <Typography variant="caption" color="textSecondary" component="p">
              Loading...
            </Typography>
          )}

          {dosesEnd && (
            <Typography variant="caption" color="textSecondary" component="p">
              You have reached the end 😎
            </Typography>
          )}
        </Grid>
      </Grid>
      {open && (
        <AddDoseToCirclesOptionModal
          dose={selectedDose!}
          open={open}
          onClose={(isAdded) => {
            handleClose()
            isAdded && setExpanded(false)
          }}
        />
      )}
    </CustomDialogContent>
  )
}

export default FindSuggestions
